import { useState } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'

import type {
    SalesReturnFormValues,
    SalesReturnLot,
} from '@/lib/schemas/sales-return-schemas'
import { distributeFifo, lineQty } from '@/lib/schemas/sales-return-schemas'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { ShelfField } from '@/components/sections/return-shelf-field'
import { ReturnQtyStepper } from '@/components/sections/return-qty-stepper'
import { formatQty } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'

interface Props {
    index: number
}

/**
 * Một dòng hàng của popup trả hàng (M-44), dạng thẻ cho mobile. Một ô "Số
 * lượng trả" cho cả dòng (chia FIFO vào lô). Dòng nhiều lô mới hiện thêm ô
 * từng lô; hàng lỗi và kệ nhập nằm dưới từng lô khi lô đó có số trả.
 */
export function SalesReturnLine({ index }: Props) {
    const { control, setValue, getValues, getFieldState, formState } =
        useFormContext<SalesReturnFormValues>()
    const line = useWatch({ control, name: `lines.${index}` })
    const total = lineQty(line)
    const lineId = `return-line-${line.sales_order_item_id}`
    const multiLot = line.lots.length > 1
    const lineError =
        getFieldState(`lines.${index}.returnable_qty`, formState).error
            ?.message ??
        (multiLot
            ? undefined
            : getFieldState(`lines.${index}.lots.0.quantity`, formState).error
                  ?.message)

    const setLineQty = (raw: string) => {
        const lots = getValues(`lines.${index}.lots`)
        setValue(`lines.${index}.lots`, distributeFifo(lots, Number(raw)), {
            shouldDirty: true,
        })
    }

    return (
        <article className="space-y-3 rounded-lg border bg-card p-3 sm:p-4">
            <header className="min-w-0">
                <h4 className="font-medium leading-snug break-words">
                    {line.product_name}
                </h4>
                <p className="text-sm text-muted-foreground">
                    {m.sales_return_line_summary({
                        delivered: formatQty(line.quantity),
                        returned: formatQty(line.returned_qty),
                        returnable: formatQty(line.returnable_qty),
                        unit: line.unit,
                    })}
                </p>
            </header>

            <div className="flex flex-wrap items-end justify-between gap-x-3 gap-y-2">
                <div className="space-y-1.5">
                    <Label htmlFor={lineId}>
                        {m.sales_return_line_qty_label()}
                    </Label>
                    <ReturnQtyStepper
                        id={lineId}
                        label={m.sales_return_line_qty_label()}
                        value={total === 0 ? '' : String(total)}
                        onChange={setLineQty}
                        max={line.returnable_qty}
                        unit={line.unit}
                        invalid={Boolean(lineError)}
                        describedBy={lineError ? `${lineId}-error` : undefined}
                    />
                </div>
                {total < line.returnable_qty && (
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-11 text-primary"
                        onClick={() => setLineQty(String(line.returnable_qty))}
                    >
                        {m.sales_return_return_all({
                            qty: formatQty(line.returnable_qty),
                        })}
                    </Button>
                )}
            </div>
            {lineError && (
                <p id={`${lineId}-error`} className="text-sm text-destructive">
                    {lineError}
                </p>
            )}

            {line.lots.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                    {m.sales_return_no_lot()}
                </p>
            ) : (
                <div className="space-y-2">
                    {multiLot && (
                        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                            {m.sales_return_split_by_lot()}
                        </p>
                    )}
                    {line.lots.map((lot, j) => (
                        <ReturnLotRow
                            key={lot.product_lot_id}
                            base={`lines.${index}.lots.${j}`}
                            idPrefix={`${lineId}-lot-${lot.product_lot_id}`}
                            lot={lot}
                            unit={line.unit}
                            showQty={multiLot}
                        />
                    ))}
                </div>
            )}
        </article>
    )
}

interface LotRowProps {
    base: `lines.${number}.lots.${number}`
    idPrefix: string
    lot: SalesReturnLot
    unit: string
    showQty: boolean
}

function ReturnLotRow({ base, idPrefix, lot, unit, showQty }: LotRowProps) {
    const { control, setValue } = useFormContext<SalesReturnFormValues>()
    const [editGood, setEditGood] = useState(false)
    const [editDefect, setEditDefect] = useState(false)
    const qty = Number(lot.quantity) || 0
    const returning = qty > 0
    const allDefect = lot.defective && (Number(lot.defect_quantity) || 0) >= qty

    return (
        <div className="space-y-2 rounded-md bg-muted/50 p-2.5">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="min-w-0 text-sm">
                    <span className="text-muted-foreground">
                        {m.confirm_return_lot_prefix()}{' '}
                    </span>
                    <span className="font-mono text-xs break-all">
                        {lot.lot_code}
                    </span>
                    {showQty && (
                        <span className="block text-xs text-muted-foreground">
                            {m.sales_return_lot_returnable({
                                qty: formatQty(lot.returnable_qty),
                            })}
                        </span>
                    )}
                </div>
                {showQty && (
                    <Controller
                        control={control}
                        name={`${base}.quantity`}
                        render={({ field, fieldState }) => (
                            <div>
                                <ReturnQtyStepper
                                    id={`${idPrefix}-qty`}
                                    label={m.sales_return_lot_qty_label()}
                                    value={field.value}
                                    onChange={field.onChange}
                                    max={lot.returnable_qty}
                                    unit={unit}
                                    size="sm"
                                    invalid={Boolean(fieldState.error)}
                                />
                                {fieldState.error && (
                                    <p className="mt-1 text-sm text-destructive">
                                        {fieldState.error.message}
                                    </p>
                                )}
                            </div>
                        )}
                    />
                )}
            </div>

            {returning && (
                <>
                    <Controller
                        control={control}
                        name={`${base}.defective`}
                        render={({ field }) => (
                            <label className="flex min-h-11 cursor-pointer items-center justify-between gap-3 text-sm">
                                <span>{m.confirm_return_col_defect()}</span>
                                <Switch
                                    checked={field.value}
                                    onCheckedChange={(checked) => {
                                        field.onChange(checked)
                                        // Mặc định cả phần đang trả là hàng lỗi;
                                        // người dùng giảm bớt nếu chỉ một phần lỗi.
                                        if (checked && !lot.defect_quantity) {
                                            setValue(
                                                `${base}.defect_quantity`,
                                                lot.quantity,
                                            )
                                        }
                                    }}
                                />
                            </label>
                        )}
                    />
                    {lot.defective && (
                        <Controller
                            control={control}
                            name={`${base}.defect_quantity`}
                            render={({ field, fieldState }) => (
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <Label
                                        htmlFor={`${idPrefix}-defect`}
                                        className="text-sm font-normal"
                                    >
                                        {m.confirm_return_defect_qty_label()}
                                    </Label>
                                    <ReturnQtyStepper
                                        id={`${idPrefix}-defect`}
                                        label={m.confirm_return_defect_qty_label()}
                                        value={field.value}
                                        onChange={field.onChange}
                                        max={qty}
                                        unit={unit}
                                        size="sm"
                                        invalid={Boolean(fieldState.error)}
                                    />
                                    {fieldState.error && (
                                        <p className="w-full text-sm text-destructive">
                                            {fieldState.error.message}
                                        </p>
                                    )}
                                </div>
                            )}
                        />
                    )}

                    {!allDefect && (
                        <ShelfRow
                            label={m.confirm_return_position_label()}
                            summary={lot.suggested_position_label}
                            open={editGood}
                            onOpen={() => setEditGood(true)}
                        >
                            <Controller
                                control={control}
                                name={`${base}.position_id`}
                                render={({ field }) => (
                                    <ShelfField
                                        id={`${idPrefix}-position`}
                                        label={m.confirm_return_position_label()}
                                        value={field.value}
                                        onChange={field.onChange}
                                        placeholder={m.confirm_return_position_placeholder()}
                                        fallbackLabel={
                                            lot.suggested_position_label
                                        }
                                        currentPositionId={
                                            lot.current_position_id
                                        }
                                        newLot={lot.position_new_lot}
                                        newLotLabel={m.confirm_return_new_lot()}
                                        onNewLotChange={(checked) =>
                                            setValue(
                                                `${base}.position_new_lot`,
                                                checked,
                                            )
                                        }
                                    />
                                )}
                            />
                        </ShelfRow>
                    )}
                    {lot.defective && (
                        <Controller
                            control={control}
                            name={`${base}.defect_position_id`}
                            render={({ field, fieldState }) => (
                                <ShelfRow
                                    label={m.confirm_return_defect_position_label()}
                                    summary={
                                        lot.suggested_defect_position_label
                                    }
                                    open={
                                        editDefect ||
                                        Boolean(fieldState.error) ||
                                        !field.value
                                    }
                                    onOpen={() => setEditDefect(true)}
                                >
                                    <ShelfField
                                        id={`${idPrefix}-defect-position`}
                                        label={m.confirm_return_defect_position_label()}
                                        value={field.value}
                                        onChange={field.onChange}
                                        placeholder={m.confirm_return_defect_position_placeholder()}
                                        fallbackLabel={
                                            lot.suggested_defect_position_label
                                        }
                                        error={fieldState.error?.message}
                                        currentPositionId={
                                            lot.current_position_id
                                        }
                                        newLot={lot.defect_new_lot}
                                        newLotLabel={m.confirm_return_defect_new_lot()}
                                        onNewLotChange={(checked) =>
                                            setValue(
                                                `${base}.defect_new_lot`,
                                                checked,
                                            )
                                        }
                                    />
                                </ShelfRow>
                            )}
                        />
                    )}
                </>
            )}
        </div>
    )
}

interface ShelfRowProps {
    label: string
    summary?: string
    open: boolean
    onOpen: () => void
    children: React.ReactNode
}

/** Kệ nhập mặc định thu gọn thành một dòng; bấm "Đổi kệ" mới mở ô chọn. */
function ShelfRow({ label, summary, open, onOpen, children }: ShelfRowProps) {
    if (open) return <>{children}</>
    return (
        <div className="flex min-h-11 items-center justify-between gap-3 text-sm">
            <span className="min-w-0">
                <span className="text-muted-foreground">{label} </span>
                <span className="font-medium break-words">
                    {summary || m.sales_return_shelf_default()}
                </span>
            </span>
            <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-11 shrink-0 text-primary"
                onClick={onOpen}
            >
                {m.sales_return_change_shelf()}
            </Button>
        </div>
    )
}
